import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fmtFecha, hoyISO } from '../utils/fecha'
import { useAbm } from '../hooks/useAbm'
import AbmTable from '../components/AbmTable'
import Boton from '../components/Boton'
import { fotoArticuloUrl } from '../api/notasVenta'
import api from '../api/client'
import { colors, radius } from '../design/tokens'

// STOCK → Artículos. El listado es solo navegación: el alta y la edición viven
// en la FICHA (/articulos/nuevo · /articulos/{id}) con el maestro completo,
// foto, precios, estado en ligierweb y estado en Presea. El modal viejo de 4
// campos se retiró (2 jul 2026).

/** Miniatura de la foto del artículo; si no existe el archivo, se oculta sola. */
function Thumb({ sku, v }) {
  if (!sku) return <span style={{ color: colors.textPlh }}>—</span>

  return (
    <img
      src={v ? `${fotoArticuloUrl(sku)}?v=${v}` : fotoArticuloUrl(sku)}
      alt=""
      loading="lazy"
      style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: radius.xs, background: colors.bgSoft, display: 'block' }}
      onError={(e) => { e.target.style.visibility = 'hidden' }}
    />
  )
}

export default function ArticulosPage() {
  const navigate = useNavigate()
  // El listado trae SOLO los activos. Sin este interruptor, desactivar un
  // artículo lo hacía desaparecer para siempre: no había forma de encontrarlo
  // para volver a activarlo, y el trabajo quedaba trabado sin salida.
  const [verInactivos, setVerInactivos] = useState(false)
  const [seleccion, setSeleccion] = useState(() => new Set())
  const [aplicando, setAplicando] = useState(false)
  const [aviso, setAviso] = useState(null)
  const { rows, loading, search, setSearch, meta, setPage, colFilters, setColFilter, refresh } = useAbm(
    'articulos',
    { paginated: true, params: verInactivos ? { activos: 0 } : null },
  )

  const toggleFila = (id) => setSeleccion(prev => {
    const n = new Set(prev)
    n.has(id) ? n.delete(id) : n.add(id)
    return n
  })

  // Elige/deselecciona TODO lo que está a la vista (la página actual, que es lo
  // que el operario acaba de filtrar). No toca lo que no se ve.
  const toggleTodas = (filas) => setSeleccion(prev => {
    const todasElegidas = filas.length > 0 && filas.every(f => prev.has(f.id))
    const n = new Set(prev)
    filas.forEach(f => (todasElegidas ? n.delete(f.id) : n.add(f.id)))
    return n
  })

  /**
   * DISPONIBLE = se muestra o no en la tienda (Martin, 23 sep 2026).
   * `ids` puede ser uno solo (click en la columna) o el lote.
   */
  const cambiarVisibilidad = async (ids, visible) => {
    setAplicando(true)
    setAviso(null)
    try {
      const { data } = await api.post('/articulos/visibilidad-web', { ids, visible })
      setAviso({ ok: true, texto: data.mensaje })
      setSeleccion(new Set())
      await refresh()
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo cambiar la visibilidad.' })
    } finally {
      setAplicando(false)
    }
  }

  /** STOCK = se puede comprar o no: mueve las dos marcas juntas (web_en_stock + sin_stock). */
  const cambiarStockWeb = async (ids, enStock) => {
    setAplicando(true)
    setAviso(null)
    try {
      const { data } = await api.post('/articulos/stock-web', { ids, en_stock: enStock })
      setAviso({ ok: true, texto: data.mensaje })
      setSeleccion(new Set())
      await refresh()
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo cambiar el stock de la web.' })
    } finally {
      setAplicando(false)
    }
  }

  /** Cómo está el LOTE en una columna: 'si' (todos verdes), 'no' (todos rojos) o 'mezcla'. */
  const estadoLote = (predicado) => {
    const elegidas = rows.filter(r => seleccion.has(r.id))
    if (elegidas.length === 0) return 'mezcla'
    if (elegidas.every(predicado)) return 'si'
    if (elegidas.every(r => !predicado(r))) return 'no'
    return 'mezcla'
  }

  const exportar = async () => {
    const params = {
      ...(search ? { q: search } : {}),
      ...(Object.keys(colFilters).length ? { f: colFilters } : {}),
      ...(verInactivos ? { activos: 0 } : {}),
    }
    const res = await api.get('/articulos/export', { params, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = 'articulos_' + hoyISO() + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const mono = { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 13 }
  const columns = [
    { key: 'foto', label: '', w: 44, render: (_, row) => <Thumb sku={row.codigo_alfa} v={row.foto_v} /> },
    {
      key: 'codigo_presea', label: 'Cód. Presea', w: 95, filtrable: true,
      render: v => v
        ? <code style={{ ...mono, fontWeight: 500 }}>{v}</code>
        : <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', background: '#fef3c7', color: '#92400e' }}>Pendiente</span>,
    },
    { key: 'codigo_alfa', label: 'Cód. Alfa', w: 100, filtrable: true, render: v => v ? <code style={mono}>{v}</code> : '—' },
    { key: 'descripcion', label: 'Descripción', w: 260, filtrable: true },
    { key: 'rubro', filtroMultiple: true, label: 'Rubro', w: 110, render: v => v || '—' },
    { key: 'subrubro', filtroMultiple: true, label: 'Subrubro', w: 110, render: v => v || '—' },
    { key: 'tipo_prod', filtroMultiple: true, label: 'Tipo', w: 100, render: v => v || '—' },
    { key: 'familia', filtroMultiple: true, label: 'Familia', w: 100, render: v => v || '—' },
    { key: 'marca', filtroMultiple: true, label: 'Marca', w: 130, render: v => v || '—' },
    { key: 'bodega', filtroMultiple: true, label: 'Bodega', w: 130, render: v => v || '—' },
    { key: 'cepa', filtroMultiple: true, label: 'Cepa', w: 110, render: v => v || '—' },
    { key: 'anio', filtroMultiple: true, label: 'Año', w: 65, render: v => v || '—' },
    { key: 'presenta', filtroMultiple: true, label: 'Presenta', w: 90, render: v => v || '—' },
    { key: 'contenido', filtroMultiple: true, label: 'Cont.', w: 75, render: (v, row) => [v, row.bultos ? `x${row.bultos}` : null].filter(Boolean).join(' ') || '—' },
    { key: 'pais', filtroMultiple: true, label: 'País', w: 95, render: v => v || '—' },
    { key: 'nomprov', filtroMultiple: true, label: 'Proveedor', w: 150, render: v => v || '—' },
    { key: 'codigo_ean', label: 'EAN', w: 120, filtrable: true, render: v => v ? <code style={mono}>{v}</code> : '—' },
    { key: 'costo', label: 'Costo', w: 95, render: v => v != null && parseFloat(v) > 0 ? '$ ' + parseFloat(v).toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '—' },
    { key: 'dtoligierc', label: 'Dto %', w: 65, render: v => v != null && parseFloat(v) > 0 ? parseFloat(v) : '—' },
    { key: 'iva', label: 'IVA %', w: 65 },
    { key: 'factura_como', label: 'Factura como', w: 110, render: (v, row) => row.factura_como ? <code style={mono}>{row.factura_como.codigo_alfa}</code> : '—' },
    {
      // DISPONIBLE = ¿se MUESTRA en la tienda? (Martin, 23 sep 2026). Verde
      // visible / rojo no visible; un click lo cambia, sin entrar a la ficha.
      key: 'web_habilitado', label: 'Disponible', w: 108,
      render: (v, row) => (
        <TagSiNo
          si={esVisible(row)}
          textoSi="Visible" textoNo="No visible"
          title={esVisible(row)
            ? 'Se muestra en la tienda — click para dejar de mostrarlo'
            : 'No se muestra en la tienda — click para mostrarlo'}
          disabled={aplicando}
          onClick={() => cambiarVisibilidad([row.id], !esVisible(row))}
        />
      ),
    },
    {
      // STOCK = ¿se puede COMPRAR? Verde en stock / rojo sin stock. Un producto
      // puede estar visible y sin stock: se ve como agotado.
      key: 'web_en_stock', label: 'Stock', w: 96,
      render: (v, row) => (
        <TagSiNo
          si={hayStock(row)}
          textoSi="En stock" textoNo="Sin stock"
          title={hayStock(row)
            ? 'Se puede comprar — click para marcarlo sin stock'
            : 'No se puede comprar — click para marcarlo en stock'}
          disabled={aplicando}
          onClick={() => cambiarStockWeb([row.id], !hayStock(row))}
        />
      ),
    },
    // Filtros de regalo de la tienda (Martin, 6 oct 2026): los escribe una
    // persona; se cargan en lote con la barra de arriba o en la ficha.
    { key: 'web_tipo_regalo', filtroMultiple: true, label: 'Tipo de regalo (web)', w: 130, render: v => v || '—' },
    { key: 'web_filtro_regalo', filtroMultiple: true, label: 'Filtro de regalos (web)', w: 150, render: v => v || '—' },
    { key: 'sync_presea_at', label: 'Sync Presea', w: 100, render: v => v ? fmtFecha(v) : '—' },
  ]

  return (
    <>
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
      {/* Vista planilla (Martin, 6 oct 2026): editar como en Excel. */}
      <Boton sm onClick={() => navigate('/articulos/planilla')}>▦ Ver como planilla</Boton>
      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
        fontSize: '0.8rem', color: verInactivos ? '#b45309' : '#64748b',
        background: verInactivos ? '#fffbeb' : 'transparent',
        border: `1px solid ${verInactivos ? '#fcd34d' : 'transparent'}`,
        borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer',
      }}>
        <input
          type="checkbox"
          checked={verInactivos}
          onChange={e => { setVerInactivos(e.target.checked); setPage(1) }}
          style={{ cursor: 'pointer' }}
        />
        Ver también los desactivados
      </label>
    </div>
    {aviso && (
      <div style={{
        marginBottom: '0.6rem', padding: '0.5rem 0.8rem', borderRadius: 6, fontSize: '0.82rem',
        background: aviso.ok ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${aviso.ok ? '#bbf7d0' : '#fecaca'}`,
        color: aviso.ok ? '#166534' : '#b91c1c',
      }}>{aviso.ok ? '✓ ' : '✕ '}{aviso.texto}</div>
    )}

    {/* Acción en lote: filtrás, tildás y cambiás todo junto. Aparece sola
        cuando hay algo elegido y dice CUÁNTOS son, para que nadie aplique a
        ciegas sobre una selección que no recuerda. */}
    {seleccion.size > 0 && (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        marginBottom: '0.6rem', padding: '0.6rem 0.9rem',
        background: '#1e293b', color: '#fff', borderRadius: 8,
      }}>
        <strong style={{ fontSize: '0.85rem' }}>
          {seleccion.size} {seleccion.size === 1 ? 'artículo elegido' : 'artículos elegidos'}
        </strong>
        <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Los MISMOS dos botones de la tabla (Martin, 23 sep 2026: "dos
              botones, no 4"): muestran cómo está el lote y, al tocarlos, lo
              dejan entero en el otro valor. Mezclado → los pone a todos en verde. */}
          <TagLote
            label="Disponible"
            estado={estadoLote(esVisible)}
            textoSi="Visible" textoNo="No visible"
            disabled={aplicando}
            onClick={() => cambiarVisibilidad([...seleccion], estadoLote(esVisible) !== 'si')}
          />
          <TagLote
            label="Stock"
            estado={estadoLote(hayStock)}
            textoSi="En stock" textoNo="Sin stock"
            disabled={aplicando}
            onClick={() => cambiarStockWeb([...seleccion], estadoLote(hayStock) !== 'si')}
          />
          <AsignarRegalo ids={[...seleccion]} disabled={aplicando}
            onHecho={async (texto) => { setAviso({ ok: true, texto }); setSeleccion(new Set()); await refresh() }}
            onError={(texto) => setAviso({ ok: false, texto })} />
          <Boton variante="ghost" sm onClick={() => setSeleccion(new Set())}>Cancelar</Boton>
        </div>
      </div>
    )}

    <AbmTable
      title="Artículos"
      columns={columns}
      rows={rows}
      loading={loading}
      seleccionables
      seleccion={seleccion}
      onToggleFila={toggleFila}
      onToggleTodas={toggleTodas}
      onCreate={() => navigate('/articulos/nuevo')}
      // Grilla estilo /notas-venta (pedido Martin 12 ago): sin Editar/Eliminar —
      // la fila entera entra a la ficha, con sombra al pasar. La baja vive
      // dentro de la ficha del artículo (que además desactiva, no borra).
      filaHref={(row) => `/articulos/${row.id}`}
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar por código o descripción..."
      scrollX
      columnFilters={colFilters}
      onColumnFilter={setColFilter}
      fetchFacetas={async (campo) => (await api.get('/articulos/facetas', { params: { campo, ...(search ? { q: search } : {}), ...(Object.keys(colFilters).length ? { f: colFilters } : {}) } })).data}
      onExport={exportar}
      pagination={meta}
      onPageChange={setPage}
    />
    </>
  )
}

/**
 * Filtros de regalo en lote: elegís el filtro, escribís (o elegís) el valor que
 * ve el cliente y se aplica a los artículos tildados. Vacío = sale del filtro.
 */
function AsignarRegalo({ ids, disabled, onHecho, onError }) {
  const [campo, setCampo] = useState('web_tipo_regalo')
  const [valor, setValor] = useState('')
  const [valores, setValores] = useState([])
  const [enviando, setEnviando] = useState(false)
  useEffect(() => {
    api.get('/articulos/facetas', { params: { campo } }).then(({ data }) => setValores(data.map(x => x.valor ?? x.value ?? x))).catch(() => setValores([]))
  }, [campo])
  const aplicar = async () => {
    setEnviando(true)
    try {
      const { data } = await api.post('/articulos/filtros-regalo', { ids, campo, valor: valor.trim() || null })
      setValor('')
      await onHecho(data.mensaje)
    } catch (e) {
      onError(e.response?.data?.message || 'No se pudo asignar.')
    } finally { setEnviando(false) }
  }
  const lista = 'lista-regalo-' + campo
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
      <select value={campo} onChange={e => setCampo(e.target.value)} disabled={disabled || enviando}
        style={{ fontSize: 12, padding: '2px 4px', borderRadius: 4 }} aria-label="Filtro de regalo">
        <option value="web_tipo_regalo">Tipo de regalo</option>
        <option value="web_filtro_regalo">Filtro de regalos</option>
      </select>
      <input list={lista} value={valor} onChange={e => setValor(e.target.value)} placeholder="Valor (vacío = quitar)"
        style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid #cbd5e1', width: 170 }} aria-label="Valor" />
      <datalist id={lista}>{valores.map(v => <option key={String(v)} value={String(v)} />)}</datalist>
      <Boton sm disabled={disabled || enviando} onClick={aplicar}>{enviando ? 'Aplicando…' : 'Asignar'}</Boton>
    </span>
  )
}

/** Tag verde/rojo de la tabla: un click lo cambia. */
function TagSiNo({ si, textoSi, textoNo, title, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      style={{ ...tagBase(disabled), ...(si ? tagVerde : tagRojo) }}
    >
      {si ? textoSi : textoNo}
    </button>
  )
}

/** El mismo tag, para el lote: dice cómo está la selección y la deja toda en el otro valor. */
function TagLote({ label, estado, textoSi, textoNo, disabled, onClick }) {
  const estilo = estado === 'si' ? tagVerde : (estado === 'no' ? tagRojo : tagMezcla)
  const texto = estado === 'si' ? textoSi : (estado === 'no' ? textoNo : 'Mezclados')
  const destino = estado === 'si' ? textoNo : textoSi
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
      <span style={{ color: '#cbd5e1' }}>{label}</span>
      <button
        type="button"
        disabled={disabled}
        title={`Dejar todo el lote en "${destino}"`}
        onClick={onClick}
        style={{ ...tagBase(disabled), ...estilo }}
      >
        {texto} → {destino}
      </button>
    </span>
  )
}

const tagBase = (disabled) => ({
  padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
  cursor: disabled ? 'wait' : 'pointer', border: '1px solid', whiteSpace: 'nowrap',
})
const tagVerde = { borderColor: '#bbf7d0', background: '#f0fdf4', color: '#166534' }
const tagRojo = { borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }
const tagMezcla = { borderColor: '#e2e8f0', background: '#f8fafc', color: '#475569' }

/** DISPONIBLE: se muestra en la tienda (habilitado + un modo de visibilidad que se ve). */
const esVisible = (row) => row.web_habilitado === true && [2, 4].includes(Number(row.web_visibilidad))
/** STOCK: se puede comprar (en stock en la web y sin el veto de la casa). */
const hayStock = (row) => row.web_en_stock === true && !row.sin_stock
